/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { StarterInfo } from '../../../common/types';
import { validatePhone } from '../../../common/utils';
import { SubText, MaskType } from '@motili/ui';
import { useAppDispatch } from '../../../common/redux/hooks';
import { setStarterInfo } from '../../../common/redux/ducks/clientRegistrationForm';
import StyledMaskedTextInput from '../../../components/styled/StyledMaskedTextInput';
import StyledTextInput from '../../../components/styled/StyledTextInput';

interface GetStartedFormProps {
    starterInfo: StarterInfo;
}

export default function GetStartedForm({
    starterInfo,
}: GetStartedFormProps): ReactElement {
    const { firstName, lastName, email, phone } = starterInfo;
    const dispatch = useAppDispatch();
    const {
        register,
        formState: { errors },
    } = useForm({ mode: 'onBlur' });

    const handleInputChange = ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => {
        if (name === 'phone') {
            value = value.replaceAll(/\D/g, '');
        }
        dispatch(setStarterInfo({ ...starterInfo, [name]: value }));
    };

    return (
        <form>
            <StyledTextInput
                marginTop='20px'
                required
                name={'firstName'}
                error={!!errors.firstName}
                errorMessage={'First Name cannot be blank'}
                register={register('firstName', {
                    required: true,
                })}
                onChange={handleInputChange}
                value={firstName}
                style={inputStyles}
                label='First Name'
            />
            <StyledTextInput
                marginTop='20px'
                required
                name={'lastName'}
                error={!!errors.lastName}
                errorMessage={'Last Name cannot be blank'}
                register={register('lastName', {
                    required: true,
                })}
                onChange={handleInputChange}
                value={lastName}
                style={inputStyles}
                label='Last Name'
            />
            <StyledTextInput
                marginTop='20px'
                required
                name={'email'}
                type='email'
                error={!!errors.email}
                errorMessage={'Please provide a valid email address'}
                register={register('email', {
                    required: true,
                    pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Entered value does not match email format',
                    },
                })}
                onChange={handleInputChange}
                value={email}
                style={inputStyles}
                label='Email Address'
            />
            <SubText>
                This will be used as your username and cannot be edited later.
            </SubText>
            <StyledMaskedTextInput
                marginTop='20px'
                required
                name={'phone'}
                error={!!errors.phone}
                errorMessage={'Please enter a valid phone'}
                style={inputStyles}
                register={register('phone', {
                    required: true,
                    validate: validatePhone,
                })}
                onChange={handleInputChange}
                value={phone}
                label='Phone Number'
                mask={MaskType.PHONE}
            />
        </form>
    );
}

const inputStyles = { width: '100%', margin: '10px 0' };
